// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xBaseTheme, xBlueprintName } from "@pacific/XDL/types";
import { css } from "styled-components";

export const fontConfig = {
    url: "https://fonts.cdnfonts.com/css/rawline?styles=28862,28864,28866&display=swap",
    weight: {
        medium: 500,
        semiBold: 600,
        bold: 700,
    },
    fontFamily: "'Rawline', sans-serif",
};

export const headingsFontConfig = {
    pacific: {
        url: "https://fonts.googleapis.com/css2?family=Roboto+Slab:wght@400;500;600;700;800&display=swap",
        weight: {
            regular: 400,
            bold: 700,
            extraBold: 800,
        },
        fontFamily: "'Roboto Slab', serif",
    },
    mastersource: {
        url: "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap",
        weight: {
            regular: 400,
            bold: 700,
            extraBold: 800,
        },
        fontFamily: "'Montserrat', sans-serif",
    },
};

export default {
    fontFamilyImportUrl: "",
    body: {
        size: "15px",
        weight: fontConfig.weight.medium,
        fontFamily: fontConfig.fontFamily,
    },
    p: {
        size: "15px",
        weight: fontConfig.weight.medium,
        fontFamily: fontConfig.fontFamily,
        css: css`
            margin-bottom: 20px;
            letter-spacing: 0;
            line-height: 24px;
        `,
    },
    h1: {
        size: "32px",
        weight: headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].weight.extraBold,
        fontFamily: headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].fontFamily,
        css: css`
            margin-bottom: 24px;
            letter-spacing: 0;
            line-height: 40px;
        `,
    },
    h2: {
        size: "28px",
        weight: headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].weight.extraBold,
        fontFamily: headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].fontFamily,
        css: css`
            margin-bottom: 16px;
            letter-spacing: 0;
            line-height: 34px;
        `,
    },
    h3: {
        size: "24px",
        weight: headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].weight.extraBold,
        fontFamily: headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].fontFamily,
        css: css`
            margin-bottom: 12px;
            letter-spacing: 0;
            line-height: 32px;
        `,
    },
    h4: {
        size: "20px",
        weight: headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].weight.bold,
        fontFamily: headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].fontFamily,
        css: css`
            margin-bottom: 12px;
            letter-spacing: 0;
            line-height: 25px;
        `,
    },
    h5: {
        size: "18px",
        weight: headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].weight.bold,
        fontFamily: headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].fontFamily,
        css: css`
            margin-bottom: 24px;
            letter-spacing: 0;
            line-height: 22px;
        `,
    },
    h6: {
        size: "14px",
        weight: headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].weight.bold,
        fontFamily: headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].fontFamily,
        css: css`
            margin-bottom: 8px;
            letter-spacing: 0;
            line-height: 19px;
        `,
    },
    headerPrimary: {
        forwardAs: "p",
        size: "24px",
        fontFamily: fontConfig.fontFamily,
        color: "text.main",
        transform: "uppercase",
    },
    headerSecondary: {
        forwardAs: "p",
        size: "15px",
        fontFamily: fontConfig.fontFamily,
        transform: "uppercase",
        weight: 800,
    },
    headerTertiary: {
        forwardAs: "p",
        size: "15px",
        fontFamily: fontConfig.fontFamily,
        color: "text.main",
        transform: "uppercase",
    },
    legend: { forwardAs: "span", size: "10px", color: "text.accent", transform: "uppercase", weight: 600 },
} as xBaseTheme["typography"];
